import { Directive, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { TokenVaultFilterKey, TokenVaultSummaryDto } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { CoreNiceLabelUtils } from '@workspace/ng-data-access';
import { Observable } from 'rxjs';

export type TokenVaultColumnKey =
  | 'context'
  | 'action'
  | 'verification'
  | 'token'
  | 'status'
  | 'expireDate'
  | 'usedDate'
  | 'createdAt'
  | 'updatedAt';

@Directive()
export abstract class AbstractTokenVaultsDataTableCoreComponent<T extends TokenVaultSummaryDto>
  extends BaseDataTableComponent<T, TokenVaultColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<TokenVaultColumnKey>[]>([
    { key: 'context', label: 'Contexte', display: true },
    { key: 'action', label: 'Action', display: true },
    { key: 'verification', label: 'Canal', display: true },
    { key: 'token', label: 'Jeton / Code', display: true },
    { key: 'status', label: 'Statut', display: true },
    { key: 'expireDate', label: "Date d'expiration", display: true },
    { key: 'usedDate', label: "Date d'utilisation", display: false },
    { key: 'createdAt', label: 'Date de création', display: true },
    { key: 'updatedAt', label: 'Date de mise à jour', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (tokenVault: TokenVaultSummaryDto) => this.navigateToTokenVault(tokenVault),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<TokenVaultColumnKey>[]>([
    {
      label: 'Action',
      column: TokenVaultFilterKey.Action,
    },
    {
      label: 'Statut',
      column: TokenVaultFilterKey.Status,
    },
    {
      label: 'Canal de vérification',
      column: TokenVaultFilterKey.Verification,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const action = params.getAll(TokenVaultFilterKey.Action);
      const status = params.getAll(TokenVaultFilterKey.Status);
      const verification = params.getAll(TokenVaultFilterKey.Verification);
      this.filterChanged({
        changed: true,
        filter: {
          action: action.length ? action : [],
          status: status.length ? status : [],
          verification: verification.length ? verification : [],
        },
      });
    });
  }

  navigateToTokenVault(tokenVault: TokenVaultSummaryDto): void {
    void this.router.navigate(['/token-vaults', tokenVault._id]);
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;

  override getDisplayForEntry(key: TokenVaultColumnKey, rawValue: string): string {
    switch (key) {
      case 'action':
        return CoreNiceLabelUtils.tokenVaultAction(rawValue);
      case 'status':
        return CoreNiceLabelUtils.tokenVaultStatus(rawValue);
      case 'verification':
        return CoreNiceLabelUtils.tokenVaultVerification(rawValue);
      default:
        return key;
    }
  }
}
